import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { tourLayout } from "./tourLayout";
import "./tour.css";

const readViewport = () => ({
  width: window.visualViewport?.width || window.innerWidth,
  height: window.visualViewport?.height || window.innerHeight,
  top: window.visualViewport?.offsetTop || 0,
  left: window.visualViewport?.offsetLeft || 0,
});

export function TourOverlay({ step, index, total, onNext, onClose, onBack, onAction, interactive = false, nextLabel }) {
  const titleId = useId();
  const cardRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState(readViewport);
  const [cardHeight, setCardHeight] = useState(260);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const update = () => setViewport(readViewport());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  useLayoutEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    const measure = () => setCardHeight(node.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [step, collapsed]);

  useEffect(() => {
    setCollapsed(false);
    let frame;
    let previous = "";
    let revealed = null;
    const measure = () => {
      const el = step?.target && document.querySelector(`[data-tour="${step.target}"]`);
      if (el && el.getClientRects().length) {
        if (revealed !== el) {
          el.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
          revealed = el;
        }
        const r = el.getBoundingClientRect();
        const key = `${r.top}|${r.left}|${r.width}|${r.height}`;
        if (key !== previous) {
          previous = key;
          setRect({ top:r.top, left:r.left, width:r.width, height:r.height });
        }
      } else if (previous !== "none") {
        previous = "none";
        setRect(null);
      }
      frame = requestAnimationFrame(measure);
    };
    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [step]);

  useEffect(() => {
    const keydown = e => {
      // Enter and Space belong to the focused control, especially during a demo.
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [onClose]);

  if (!step) return null;
  const visible = rect && rect.top < viewport.top + viewport.height && rect.top + rect.height > viewport.top;
  const position = tourLayout(viewport, visible ? rect : null, cardHeight);

  return (
    <div className={`tour-layer${interactive ? " tour-interactive" : ""}`}>
      {!interactive && !visible && <div className="tour-backdrop" />}
      {visible && <div className="tour-highlight" style={{ top:rect.top - 5, left:Math.max(2, rect.left - 5), width:Math.min(rect.width + 10, viewport.width - 4), height:rect.height + 10 }} />}
      <section ref={cardRef} className="tour-card" role="dialog" aria-label={step.title} aria-labelledby={titleId} style={position}>
        <header className="tour-card-header">
          <span>PASSO {index + 1} DE {total}</span>
          <div>
            {interactive && <button type="button" onClick={() => setCollapsed(v => !v)} aria-expanded={!collapsed}>{collapsed ? "Expandir" : "Minimizar"}</button>}
            {onClose && <button type="button" onClick={onClose} aria-label="Fechar tutorial">✕</button>}
          </div>
        </header>
        <h2 id={titleId}>{step.icon && <span aria-hidden="true">{step.icon} </span>}{step.title}</h2>
        {!collapsed && <p>{step.text}</p>}
        {step.action && onAction && <button type="button" className="tour-contact" onClick={() => onAction(step.action)}>{step.actionLabel}</button>}
        <footer>
          {onBack && <button type="button" className="tour-back" onClick={onBack}>Voltar</button>}
          <button type="button" className="tour-next" onClick={onNext}>{nextLabel || (index === total - 1 ? "Concluir tutorial" : "Próximo →")}</button>
        </footer>
      </section>
    </div>
  );
}
